import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useHeader } from "../../../Providers/HeaderContextProvider";
import {
  corporateSignupOtp,
  corporateSignupVerify,
} from "../../../Services/auth";
import amplitudeService from "../../../Services/amplitude";
import useCorporateBranding from "../hooks/useCorporateBranding";
import CorporateHeader from "../components/CorporateHeader";
import CorporateLoadingShell from "../components/CorporateLoadingShell";
import StepperPills from "../components/StepperPills";
import UnavailablePage from "../components/UnavailablePage";
import AuthOtpForm from "../../../Components/AuthLayout/AuthOtpForm";
import "../Activation/style.css";

const SESSION_KEY = "corporate_signup_session";

const readSignupSession = (locationState) => {
  if (locationState?.otp_session_id) return locationState;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const SetupPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { setHeaderContent } = useHeader();
  const { branding, content, loading, notFound } =
    useCorporateBranding(slug, "activation");

  const session = useMemo(() => readSignupSession(location.state), [location.state]);

  const [otpSessionId, setOtpSessionId] = useState(session?.otp_session_id);
  const [workOtp, setWorkOtp] = useState(() => Array(6).fill(""));
  const [otpError, setOtpError] = useState(null);
  const [topLevelError, setTopLevelError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resentMessage, setResentMessage] = useState(null);
  const [resendTimer, setResendTimer] = useState(60);
  const [isResendTimerActive, setIsResendTimerActive] = useState(true);

  useEffect(() => {
    setHeaderContent?.(null);
  }, [setHeaderContent]);

  useEffect(() => {
    if (!loading && !session && !notFound) {
      navigate(`/enterprise/${encodeURIComponent(slug)}`, { replace: true });
    }
  }, [loading, session, notFound, navigate, slug]);

  useEffect(() => {
    if (!isResendTimerActive) return undefined;
    if (resendTimer <= 0) {
      setIsResendTimerActive(false);
      return undefined;
    }
    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isResendTimerActive, resendTimer]);

  const handleOtpChange = (next) => {
    setWorkOtp(next);
    if (otpError) setOtpError(null);
    if (next.every((d) => d) && !submitting) {
      submitOtp(next.join(""));
    }
  };

  const themeStyle = {
    "--corporate-primary": branding?.primary_color || "#4377fc",
    "--corporate-secondary": branding?.secondary_color || "#0d5eaf",
    "--corporate-font-scale":
      typeof branding?.content_font_scale === "number"
        ? branding.content_font_scale
        : 1,
  };

  if (notFound) return <UnavailablePage />;
  if (loading || !session)
    return <CorporateLoadingShell branding={branding} themeStyle={themeStyle} />;

  const sections = content?.sections || {};
  const stepLabels = sections.step_labels?.labels || ["Verify", "Set up", "Start using"];
  const sponsorLine =
    sections.footer?.sponsor_line ||
    (branding?.display_name || branding?.name
      ? `Sponsored by ${branding.display_name || branding.name}`
      : "");
  const copyrightLine = sections.footer?.copyright_line || "© 2026 Aris";

  const restartFlow = () => {
    sessionStorage.removeItem(SESSION_KEY);
    navigate(`/enterprise/${encodeURIComponent(slug)}`, { replace: true });
  };

  const submitOtp = async (otpValue) => {
    if (submitting) return;
    setOtpError(null);
    setTopLevelError(null);
    setResentMessage(null);

    if (otpValue.length !== 6) {
      setOtpError("Enter the 6-digit code.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await corporateSignupVerify({
        otp_session_id: otpSessionId,
        otp: otpValue,
      });

      amplitudeService.track?.("corporate_signup_completed", {
        slug,
        corporate_id: result?.corporate_id,
      });

      try {
        amplitudeService.setUserId?.(result?.user_id);
      } catch (_) {}

      sessionStorage.removeItem(SESSION_KEY);
      navigate(`/enterprise/${encodeURIComponent(slug)}/done`, {
        replace: true,
        state: { first_name: session.first_name },
      });
    } catch (err) {
      const status = err?.status_code || err?.status;
      const code = err?.payload?.code || err?.code;
      const message = err?.message || "";

      amplitudeService.track?.("corporate_signup_failed", {
        slug,
        status,
        code,
      });

      if (status === 401 && (code === "otp_invalid" || /invalid/i.test(message))) {
        setOtpError("That code is incorrect.");
      } else if (
        status === 401 &&
        (code === "otp_max_attempts" || /max.*attempts/i.test(message))
      ) {
        setTopLevelError(
          "Too many incorrect attempts. Please restart the activation."
        );
        setTimeout(restartFlow, 1500);
      } else if (
        status === 404 &&
        (code === "otp_session_not_found" || /session.*not.*found/i.test(message))
      ) {
        setTopLevelError("Your session has expired. Please restart the activation.");
        setTimeout(restartFlow, 1500);
      } else if (status === 500 && code === "provisioning_failed") {
        setTopLevelError(
          "Your account was created but we couldn't finish setting up your access. Try logging in — if features are missing, contact support."
        );
      } else {
        setTopLevelError(message || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    submitOtp(workOtp.join(""));
  };

  const onResend = async () => {
    if (isResendTimerActive || resending) return;
    setResending(true);
    setResentMessage(null);
    setOtpError(null);
    try {
      const next = await corporateSignupOtp({
        member_id: session.member_id,
        work_email: session.work_email,
        personal_email: session.personal_email,
      });
      const nextSession = {
        ...session,
        otp_session_id: next.otp_session_id,
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(nextSession));
      setOtpSessionId(next.otp_session_id);
      setResentMessage("Code resent. Check your inbox.");
      setResendTimer(60);
      setIsResendTimerActive(true);
    } catch (err) {
      const status = err?.status_code || err?.status;
      if (status === 429) {
        setOtpError(
          "You've requested too many codes — please wait an hour and try again."
        );
      } else {
        setOtpError("We couldn't resend the code. Please try again in a moment.");
      }
    } finally {
      setResending(false);
    }
  };

  const displayName = branding?.display_name || branding?.name || "";

  return (
    <div
      className="corp-activation min-h-screen flex flex-col"
      style={themeStyle}
    >
      <Helmet>
        <title>Set up your account — {displayName}</title>
        <meta name="robots" content="noindex" />
        <link
          href="https://fonts.googleapis.com/css2?family=Urbanist:ital,wght@0,400;0,500;0,600;0,700;1,500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <CorporateHeader branding={branding} />

      <main className="flex-1 flex flex-col items-center px-4 md:px-9 pt-10 md:pt-12 pb-12">
        <div className="w-full max-w-[584px] flex flex-col items-start gap-4">
          <Link
            to={`/enterprise/${encodeURIComponent(slug)}`}
            className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full bg-white border border-[#d0d5dd] text-sm font-medium text-black hover:bg-[#fafafa] transition-colors"
          >
            <ChevronLeft size={16} className="opacity-60" />
            Back
          </Link>

          <div className="w-full corp-cta-card-shadow">
            <div className="corp-cta-card-header">
              <div className="flex flex-col gap-3 text-white">
                <h1 className="text-2xl font-semibold m-0 leading-tight">
                  Enter your 6-digit code
                </h1>
                <p className="text-sm m-0 leading-relaxed text-white/95">
                  We sent a code to{" "}
                  <strong className="font-semibold">{session.work_email}</strong>.
                </p>
              </div>
              <StepperPills labels={stepLabels} activeIndex={1} />
            </div>

            <div className="bg-white border border-[#e4e4e7] border-t-0 rounded-b-[20px] px-6 pt-8 pb-10">
              <AuthOtpForm
                title={`Verify your email`}
                subtitle={
                  <>
                    We sent a six-digit confirmation code to{" "}
                    <strong>{session.work_email}</strong>
                  </>
                }
                value={workOtp}
                onValueChange={handleOtpChange}
                onSubmit={onSubmit}
                isSubmitting={submitting}
                error={!!otpError}
                accentColor={branding?.secondary_color || "#0d5eaf"}
                resend={{
                  isTimerActive: isResendTimerActive,
                  timer: resendTimer,
                  onResend,
                  isResending: resending,
                }}
              >
                {otpError && (
                  <p
                    role="alert"
                    className="text-sm text-[#b42318] font-medium text-center -mt-2 mb-4"
                  >
                    {otpError}
                  </p>
                )}
                {resentMessage && (
                  <p className="text-sm text-emerald-700 font-medium text-center -mt-2 mb-4">
                    {resentMessage}
                  </p>
                )}
                {topLevelError && (
                  <div
                    role="alert"
                    className="rounded-lg border border-[#fda29b] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318] mb-4"
                  >
                    <p className="m-0 font-medium">{topLevelError}</p>
                    {topLevelError.includes("missing, contact support") && (
                      <button
                        type="button"
                        onClick={() => navigate("/login")}
                        style={{ color: "var(--corporate-secondary)" }}
                        className="border-0 bg-transparent p-0 mt-2 inline-flex items-center text-sm font-semibold underline cursor-pointer"
                      >
                        Go to login
                      </button>
                    )}
                  </div>
                )}
              </AuthOtpForm>
            </div>
          </div>
        </div>
      </main>

      {(sponsorLine || copyrightLine) && (
        <footer className="border-t border-[#e4e4e7] px-4 md:px-9 pt-6 pb-9">
          <p className="text-sm font-medium text-[#7e7f83] m-0">
            {sponsorLine}
            {sponsorLine && copyrightLine ? " · " : ""}
            {copyrightLine}
          </p>
        </footer>
      )}
    </div>
  );
};

export default SetupPage;
